import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireTemplatesScope?: boolean;
  requireTemplatesEditor?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireSuperAdmin = false,
  requireTemplatesScope = false,
  requireTemplatesEditor = false
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="fa-solid fa-spinner fa-spin text-violet-600 text-3xl mb-4"></i>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && user?.role !== 'super_admin') {
    return <Navigate to="/401" replace />;
  }

  if (requireAdmin) {
    // Check if user has admin role OR has any admin scopes
    const hasAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
    const hasAdminScope = user?.scopes?.some(scope => 
      scope === 'admin.access' || 
      scope === 'admin.content' || 
      scope === 'admin.logs'
    );
    
    if (!hasAdminRole && !hasAdminScope) {
      return <Navigate to="/401" replace />;
    }
  }

  if (requireTemplatesScope) {
    // Check if user has any templates scope (templates.*)
    const hasTemplatesScope = user?.scopes?.some(scope => scope.startsWith('templates.'));
    
    if (!hasTemplatesScope) {
      return <Navigate to="/401" replace />;
    }
  }

  if (requireTemplatesEditor) {
    // Check if user has templates.editor scope
    const hasTemplatesEditor = user?.scopes?.includes('templates.editor');
    
    if (!hasTemplatesEditor) {
      return <Navigate to="/401" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;

